import React, { useState, useEffect } from 'react'
import {
  Loader2, AlertCircle, ChevronDown, ChevronUp, Clock, CheckCircle2,
  XCircle, MessageSquare, Lightbulb, MessageCircleQuestion, Inbox,
  Database,
} from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import supportService, {
  type SupportTicket,
  type Suggestion,
  type DataCorrectionTicket,
  TICKET_CATEGORIES,
  TICKET_PRIORITIES,
  SUGGESTION_CATEGORIES,
} from '@/services/api/supportService'
import { CORRECTABLE_FIELDS } from '@/data/correctableFields'

type ViewFilter = 'all' | 'tickets' | 'data_corrections' | 'suggestions'

const MySubmissions: React.FC = () => {
  const [tickets, setTickets] = useState<SupportTicket[]>([])
  const [suggestions, setSuggestions] = useState<Suggestion[]>([])
  const [dataCorrections, setDataCorrections] = useState<DataCorrectionTicket[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [activeView, setActiveView] = useState<ViewFilter>('all')
  const [expandedId, setExpandedId] = useState<string | null>(null)

  useEffect(() => {
    loadSubmissions()
  }, [])

  const loadSubmissions = async () => {
    setLoading(true)
    setError('')
    try {
      const [ticketRes, suggestionRes, dataCorrectionRes] = await Promise.all([
        supportService.getMyTickets(),
        supportService.getMySuggestions(),
        supportService.getMyDataCorrections(),
      ])
      setTickets(ticketRes.tickets || [])
      setSuggestions(suggestionRes.suggestions || [])
      setDataCorrections(dataCorrectionRes.tickets || [])
    } catch {
      setError('Failed to load submissions')
    } finally {
      setLoading(false)
    }
  }

  const toggleExpand = (id: string) => {
    setExpandedId(expandedId === id ? null : id)
  }

  const getStatusBadge = (statusRaw: string) => {
    // Normalise hyphenated statuses (HR tickets use "in-progress", support uses "in_progress")
    const status = statusRaw.replace(/-/g, '_')
    const config: Record<string, { className: string; icon: React.ReactNode }> = {
      pending: { className: 'bg-amber-100 text-amber-700 border-amber-200', icon: <Clock className="w-3 h-3" /> },
      in_review: { className: 'bg-blue-100 text-blue-700 border-blue-200', icon: <MessageSquare className="w-3 h-3" /> },
      in_progress: { className: 'bg-purple-100 text-purple-700 border-purple-200', icon: <Loader2 className="w-3 h-3" /> },
      resolved: { className: 'bg-green-100 text-green-700 border-green-200', icon: <CheckCircle2 className="w-3 h-3" /> },
      closed: { className: 'bg-gray-100 text-gray-600 border-gray-200', icon: <XCircle className="w-3 h-3" /> },
      escalated: { className: 'bg-red-100 text-red-600 border-red-200', icon: <AlertCircle className="w-3 h-3" /> },
      planned: { className: 'bg-indigo-100 text-indigo-700 border-indigo-200', icon: <Clock className="w-3 h-3" /> },
      implemented: { className: 'bg-green-100 text-green-700 border-green-200', icon: <CheckCircle2 className="w-3 h-3" /> },
      declined: { className: 'bg-red-100 text-red-600 border-red-200', icon: <XCircle className="w-3 h-3" /> },
    }
    const c = config[status] || config.pending
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium rounded-full border ${c.className}`}>
        {c.icon}
        {status.replace('_', ' ').toUpperCase()}
      </span>
    )
  }

  const getPriorityBadge = (priority: string) => {
    const colors: Record<string, string> = {
      low: 'bg-green-50 text-green-600',
      medium: 'bg-amber-50 text-amber-600',
      high: 'bg-orange-50 text-orange-600',
      urgent: 'bg-red-50 text-red-600',
    }
    return (
      <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${colors[priority] || colors.medium}`}>
        {priority.toUpperCase()}
      </span>
    )
  }

  const getLabelForValue = (value: string, list: { value: string; label: string }[]) => {
    return list.find(i => i.value === value)?.label || value.replace('_', ' ')
  }

  const getFieldLabel = (fieldId: string) => {
    return CORRECTABLE_FIELDS.find(f => f.id === fieldId)?.label || fieldId
  }

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr)
    return d.toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' })
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="w-6 h-6 animate-spin text-[#29ABE2]" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center p-8 text-center gap-3">
        <AlertCircle className="w-8 h-8 text-red-400" />
        <p className="text-sm text-red-600">{error}</p>
        <button onClick={loadSubmissions} className="text-sm text-[#29ABE2] hover:underline">
          Try again
        </button>
      </div>
    )
  }

  const allItems = [
    ...tickets.map(t => ({ type: 'ticket' as const, item: t, date: t.created_at, id: t.ticket_id })),
    ...dataCorrections.map(d => ({ type: 'data_correction' as const, item: d, date: d.created_at, id: d.ticket_id })),
    ...suggestions.map(s => ({ type: 'suggestion' as const, item: s, date: s.created_at, id: s.suggestion_id })),
  ].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())

  const filtered = activeView === 'all'
    ? allItems
    : allItems.filter(i => {
        if (activeView === 'tickets') return i.type === 'ticket'
        if (activeView === 'data_corrections') return i.type === 'data_correction'
        return i.type === 'suggestion'
      })

  if (allItems.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center p-10 text-center gap-3">
        <div className="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center">
          <Inbox className="w-7 h-7 text-gray-400" />
        </div>
        <p className="text-sm font-medium text-gray-600">No submissions yet</p>
        <p className="text-xs text-gray-400">Your support tickets, data corrections, and suggestions will appear here.</p>
      </div>
    )
  }

  const views: { key: ViewFilter; label: string; count: number }[] = [
    { key: 'all', label: 'All', count: allItems.length },
    { key: 'tickets', label: 'Tickets', count: tickets.length },
    { key: 'data_corrections', label: 'Data Corrections', count: dataCorrections.length },
    { key: 'suggestions', label: 'Suggestions', count: suggestions.length },
  ]

  return (
    <div className="flex flex-col h-full">
      {/* Filter tabs */}
      <div className="flex gap-1 px-4 pt-3 pb-2 flex-shrink-0 flex-wrap">
        {views.map(view => (
          <button
            key={view.key}
            onClick={() => setActiveView(view.key)}
            className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
              activeView === view.key
                ? 'bg-[#15305D] text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {view.label} ({view.count})
          </button>
        ))}
      </div>

      {/* Items list */}
      <div className="flex-1 overflow-y-auto px-4 pb-4 space-y-2">
        {filtered.map(({ type, item, id }) => {
          const isExpanded = expandedId === id
          const isTicket = type === 'ticket'
          const isDataCorrection = type === 'data_correction'
          const isSuggestion = type === 'suggestion'
          const ticket = isTicket ? (item as SupportTicket) : null
          const dc = isDataCorrection ? (item as DataCorrectionTicket) : null
          const suggestion = isSuggestion ? (item as Suggestion) : null

          const hasResponse = isTicket
            ? !!(ticket?.admin_notes || ticket?.resolution_notes)
            : isDataCorrection
              ? !!dc?.resolution_notes
              : !!suggestion?.admin_response

          return (
            <div
              key={id}
              className="border rounded-lg bg-white hover:border-gray-300 transition-colors"
            >
              {/* Header */}
              <button
                onClick={() => toggleExpand(id)}
                className="w-full p-3 flex items-start gap-3 text-left"
              >
                <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                  isDataCorrection ? 'bg-amber-100' : isTicket ? 'bg-blue-100' : 'bg-amber-100'
                }`}>
                  {isDataCorrection
                    ? <Database className="w-4 h-4 text-amber-600" />
                    : isTicket
                      ? <MessageCircleQuestion className="w-4 h-4 text-green-600" />
                      : <Lightbulb className="w-4 h-4 text-amber-600" />
                  }
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs font-mono text-gray-400">{id}</span>
                    {isTicket && ticket && getPriorityBadge(ticket.priority)}
                    {getStatusBadge(
                      isTicket ? ticket!.status
                        : isDataCorrection ? dc!.status
                          : suggestion!.status
                    )}
                  </div>
                  <p className="text-sm font-medium text-gray-900 mt-0.5 truncate">
                    {isTicket
                      ? ticket!.subject
                      : isDataCorrection
                        ? `Data Correction: ${dc!.affected_fields.map(getFieldLabel).join(', ')}`
                        : suggestion!.title
                    }
                  </p>
                  <p className="text-xs text-gray-400 mt-0.5">
                    {formatDate(
                      isTicket ? ticket!.created_at
                        : isDataCorrection ? dc!.created_at
                          : suggestion!.created_at
                    )}
                    {' · '}
                    {isTicket
                      ? getLabelForValue(ticket!.category, TICKET_CATEGORIES)
                      : isDataCorrection
                        ? 'Data Problem'
                        : getLabelForValue(suggestion!.category, SUGGESTION_CATEGORIES)
                    }
                  </p>
                </div>

                <div className="flex items-center gap-1 flex-shrink-0">
                  {hasResponse && (
                    <Badge variant="outline" className="text-[10px] text-green-600 border-green-200 bg-green-50">
                      Response
                    </Badge>
                  )}
                  {isExpanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                </div>
              </button>

              {/* Expanded Detail */}
              {isExpanded && (
                <div className="px-3 pb-3 border-t">
                  <div className="pt-3 space-y-3">
                    {/* Affected Fields (data corrections only) */}
                    {isDataCorrection && dc && dc.affected_fields.length > 0 && (
                      <div>
                        <p className="text-xs font-medium text-gray-500 mb-1">Affected Fields</p>
                        <div className="flex flex-wrap gap-1">
                          {dc.affected_fields.map(f => (
                            <span key={f} className="inline-flex text-[11px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 border border-amber-200">
                              {getFieldLabel(f)}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Description */}
                    <div>
                      <p className="text-xs font-medium text-gray-500 mb-1">Description</p>
                      <p className="text-sm text-gray-700 whitespace-pre-wrap">
                        {isTicket
                          ? ticket!.description
                          : isDataCorrection
                            ? dc!.user_description
                            : suggestion!.description
                        }
                      </p>
                    </div>

                    {/* Additional fields */}
                    {isTicket && ticket?.current_page && (
                      <div>
                        <p className="text-xs font-medium text-gray-500 mb-0.5">Reported from</p>
                        <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">{ticket.current_page}</code>
                      </div>
                    )}

                    {isSuggestion && suggestion?.impact_area && (
                      <div>
                        <p className="text-xs font-medium text-gray-500 mb-0.5">Impact Area</p>
                        <p className="text-sm text-gray-700">{suggestion.impact_area}</p>
                      </div>
                    )}

                    {isSuggestion && suggestion?.business_value && (
                      <div>
                        <p className="text-xs font-medium text-gray-500 mb-0.5">Business Value</p>
                        <p className="text-sm text-gray-700">{suggestion.business_value}</p>
                      </div>
                    )}

                    {/* Admin Response / Resolution Notes */}
                    {hasResponse && (
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                        <p className="text-xs font-medium text-blue-700 mb-1 flex items-center gap-1">
                          <MessageSquare className="w-3 h-3" /> {isDataCorrection ? 'Resolution Notes' : 'Admin Response'}
                        </p>
                        <p className="text-sm text-blue-900 whitespace-pre-wrap">
                          {isTicket
                            ? (ticket!.resolution_notes || ticket!.admin_notes)
                            : isDataCorrection
                              ? dc!.resolution_notes
                              : suggestion!.admin_response
                          }
                        </p>
                      </div>
                    )}

                    {/* Assigned to */}
                    {((isTicket && ticket?.assigned_to_name) || (isDataCorrection && dc?.assigned_to_name)) && (
                      <p className="text-xs text-gray-500">
                        Assigned to: <span className="font-medium text-gray-700">
                          {isTicket ? ticket!.assigned_to_name : dc!.assigned_to_name}
                        </span>
                      </p>
                    )}

                    {/* Resolved date */}
                    {((isTicket && ticket?.resolved_at) || (isDataCorrection && dc?.resolved_at)) && (
                      <p className="text-xs text-gray-500">
                        Resolved: {formatDate(isTicket ? ticket!.resolved_at! : dc!.resolved_at!)}
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default MySubmissions
